import React from 'react'
import {
  Button,
  Popup,
  Responsive
} from 'semantic-ui-react'
// Local
import {
  REVIEW,
  shouldRenderReskin2025
} from '../../../constant'
import { toKebabCase } from '../../v3/utils/utils'
import ReveleerStatusLabel, { reveleerStatusLabelType } from '../../ReveleerStatusLabel'

/**
 * Provides an icon indicating review status. The icon should be generic per
 * user-role - it is just explaining the current status of the review
 * @param review Review entity with reviewCode property
 */

type Props = {
  reviewCode: keyof typeof REVIEW.CODE | null
}
const { CODE } = REVIEW

const lookup = (
  shouldReskin: boolean,
  reviewCode: string
) => {
  switch (reviewCode) {
    case CODE.OPEN:
      return {
        code: shouldRenderReskin2025 ? 'Open' : 'O',
        tooltip: 'This review is OPEN for CDI'
      }
    case CODE.READY_FOR_CDI_QA:
      return {
        code: shouldRenderReskin2025 ? 'CDI QA' : 'CDQ',
        tooltip: 'This review has been reviewed by CDI and is ready for QA'
      }
    case CODE.READY_FOR_PHYSICIAN:
      return {
        code: shouldRenderReskin2025 ? 'Physician' : 'P',
        tooltip: 'This review has been reviewed by CDI and QA\'d and is ready for PHYSICIAN'
      }
    case CODE.READY_FOR_CODER:
      return {
        code: shouldRenderReskin2025 ? 'Coder' : 'CO',
        tooltip: 'This review has been seen by the PHYSICIAN and is ready for CODER'
      }
    case CODE.READY_FOR_CODER_QA:
      return {
        code: shouldRenderReskin2025 ? 'Coder QA' : 'COQ',
        tooltip: 'This review has been reviewed by CODER and is ready for QA'
      }
    case CODE.COMPLETED:
      return {
        code: shouldRenderReskin2025 ? 'Completed' : 'C',
        tooltip: 'This review has been COMPLETED'
      }
    case CODE.REMOVED:
      return {
        code: shouldRenderReskin2025 ? 'Removed' : 'R',
        tooltip: 'This review has been REMOVED'
      }
    case CODE.LOCKED:
      return {
        code: shouldRenderReskin2025 ? 'Locked' : 'L',
        tooltip: 'This review is LOCKED'
      }
    default:
      return {
        code: shouldRenderReskin2025 ? 'Open' : 'O',
        tooltip: 'This review is OPEN for CDI'
      }
  }
}

function ReviewStatusIcon(
  props: Props
): JSX.Element | null {
  const {
    reviewCode
  } = props
  if (!reviewCode) return null

  const tag = lookup(shouldRenderReskin2025, reviewCode)
  const className = shouldRenderReskin2025
    ? `${toKebabCase(tag.code)} reskin`
    : toKebabCase(tag.code)
  const getStatusLabelType = (
    tagLabel: string
  ): keyof typeof reveleerStatusLabelType => {
    const lowerCasedLabel = tagLabel.split(' ').map(v => v.toLowerCase()).join(' ')
    if (lowerCasedLabel === 'open') return 'start'
    if (lowerCasedLabel === 'cdi qa') return 'blocked'
    if (lowerCasedLabel === 'physician') return 'success'
    return 'start'
  }
  return (
    <Popup
      content={ tag.tooltip }
      trigger={
        shouldRenderReskin2025
          ? (
            <ReveleerStatusLabel
              label={ tag.code }
              statusLabelType={ getStatusLabelType(tag.code) }
            />
          )
          : (
            <Responsive
              as={ Button }
              icon
              basic
              size="mini"
              className={ className }
            >
              <strong>
                { tag.code }
              </strong>
            </Responsive>
          )
      }
    />
  )
}

export default ReviewStatusIcon
